import { inject } from '@angular/core'
import { CanActivateFn, CanMatchFn, Router, UrlTree } from '@angular/router'
import { Store } from '@ngxs/store'
import { filter, map, Observable, take } from 'rxjs'
import { UserState } from '../../../store/user/user.state'
import { isRpmUserType } from '../data/role-resolution.config'
import { RouteName } from '../../../core/enums/route-name.enum'
import { IsAuthenticated } from '../../../core/guards/is-authenticated.guard'

/**
 * PA route gate on user TYPE: non-RPM users (caregivers, patients) are redirected to the patients list.
 * `userGuard` cannot do this: a caregiver carries `Permission.User` and passes it, while RPM users are
 * `Permission.Admin`, which it rejects. Waits for the first non-null `_userType` (null for a tick after
 * login) so a real RPM user's deep link is not bounced.
 */
function rpmUserAllowed$(): Observable<boolean | UrlTree> {
  const store = inject(Store)
  const router = inject(Router)

  return store.select(UserState.currentUser).pipe(
    map(
      user => (user as { _userType?: string | null } | null)?._userType ?? null
    ),
    filter((userType): userType is string => userType != null),
    take(1),
    map(userType =>
      isRpmUserType(userType) ? true : router.parseUrl('/' + RouteName.Patients)
    )
  )
}

/** Activation gate; runs after `paFlagGuard`. */
export const rpmUserGuard: CanActivateFn = (): Observable<boolean | UrlTree> =>
  rpmUserAllowed$()

/** Lazy-load gate: blocks a deep link before the chunk loads. */
export const rpmUserCanMatch: CanMatchFn = (): Observable<boolean | UrlTree> =>
  rpmUserAllowed$()

/**
 * canMatch adapter for the `IsAuthenticated` class guard (its canLoad never runs for loadComponent
 * routes). Listed first so a logged-out deep link redirects to login instead of waiting for a profile.
 */
export const isAuthenticatedCanMatch: CanMatchFn = (): boolean =>
  inject(IsAuthenticated).canActivate()
